import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import type { User, Session, AuthError } from '@supabase/supabase-js';
import { recordMultipleConsents, type ConsentType } from '../services/consentService';
// NOTE: EmailService removed - emails should only be sent from backend (Edge Functions)
// import { EmailService } from '../services/emailService';

// GDPR Consent data structure for signup
export interface SignupConsents {
  terms: boolean;
  privacy: boolean;
  ai_analysis: boolean;
  third_party: boolean;
  marketing: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, consents?: SignupConsents) => Promise<{ error: AuthError | null; needsEmailConfirmation: boolean }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const trackSession = async () => {
      try {
        await supabase.functions.invoke('track-session');
      } catch (err) {
        console.error('Failed to track session:', err);
      }
    };

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        trackSession();
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
        trackSession();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string, consents?: SignupConsents) => {
    const { error, data } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: undefined, // Prevent automatic redirect
      }
    });

    // Send welcome email via Edge Function (keeps API key secure)
    if (!error && data.user) {

      // Record GDPR consents if provided
      if (consents) {
        try {
          const consentData: Partial<Record<ConsentType, boolean>> = {
            terms: consents.terms,
            privacy: consents.privacy,
            ai_analysis: consents.ai_analysis,
            third_party: consents.third_party,
            marketing: consents.marketing
          };
          await recordMultipleConsents(consentData);
        } catch (err) {
          console.error('Failed to record consents:', err);
          // Don't block signup if consent recording fails
        }
      }

      const emailPrefix = email.split('@')[0];
      const clinicianName = emailPrefix
        .split(/[._-]/)
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      // Call Edge Function to send welcome email (async, don't block signup)
      fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-welcome-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          email: email,
          clinicianName: clinicianName || 'Usuario',
          language: localStorage.getItem('psymetrika_language') || 'es',
        }),
      }).catch((err) => {
        console.error('Failed to send welcome email:', err);
      });

      fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-admin-new-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          email: email,
          name: clinicianName || 'Usuario',
          source: 'self_signup',
        }),
      }).catch((err) => {
        console.error('Failed to notify admin for new user:', err);
      });
    }

    // needsEmailConfirmation = true when Supabase requires email confirmation before login
    // In that case data.session is null even on successful signup
    const needsEmailConfirmation = !error && !data.session;
    return { error, needsEmailConfirmation };
  };

  const signOut = async () => {
    // Clear any cached data in localStorage before signing out
    localStorage.removeItem('psychometric_pro_results');
    localStorage.removeItem('psychometric_patients');
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  };

  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
